(() => {
  const previous = window.bfBlogCompleteVisuals;
  previous?.abort();
  const lifecycle = new AbortController();
  const roots = new Map();
  const listen = (target, type, handler, signal, options = {}) => target.addEventListener(type, handler, { ...options, signal });

  function initialize(root) {
    roots.get(root)?.abort();
    const controller = new AbortController();
    roots.set(root, controller);
    const dialog = root.querySelector('[data-bf-visual-dialog]');
    if (!(dialog instanceof HTMLDialogElement) || typeof dialog.showModal !== 'function') return;
    const pan = dialog.querySelector('[data-bf-visual-pan]');
    const title = dialog.querySelector('[data-bf-visual-title]');
    const zoomIn = dialog.querySelector('[data-bf-visual-zoom-in]');
    const zoomOut = dialog.querySelector('[data-bf-visual-zoom-out]');
    const scale = dialog.querySelector('[data-bf-visual-scale]');
    if (!pan || !title || !zoomIn || !zoomOut) return;
    let opener;
    let image;
    let zoom = 1;
    let fitWidth = 720;
    let previousOverflow;
    let dragging;

    function applyZoom(nextZoom) {
      if (!image) return;
      zoom = Math.max(1, Math.min(4, nextZoom));
      image.style.width = `${Math.round(fitWidth * zoom)}px`;
      zoomIn.disabled = zoom >= 4;
      zoomOut.disabled = zoom <= 1;
      pan.dataset.zoomed = String(zoom > 1);
      if (scale) scale.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'percent', maximumFractionDigits: 0 }).format(zoom);
    }

    function fit() {
      if (!image) return;
      fitWidth = Math.min(image.naturalWidth || Number(image.width) || 720, pan.clientWidth);
      applyZoom(1);
      pan.scrollTo(0, 0);
    }

    function restore() {
      if (previousOverflow !== undefined) {
        document.documentElement.style.overflow = previousOverflow;
        previousOverflow = undefined;
      }
      dragging = undefined;
      pan.dataset.dragging = 'false';
      if (opener?.isConnected) opener.focus({ preventScroll: true });
      opener = undefined;
    }

    root.querySelectorAll('[data-visual-open]').forEach(button => {
      button.removeAttribute('aria-pressed');
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-controls', dialog.id);
      const region = button.closest('figure')?.querySelector('.bf-visual-pan');
      region?.setAttribute('tabindex', '0');
    });

    listen(root, 'click', event => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-visual-open]');
      if (!button || !root.contains(button)) return;
      const figure = button.closest('figure');
      const original = figure?.querySelector('img');
      if (!(original instanceof HTMLImageElement)) return;
      opener = button;
      title.textContent = figure.querySelector('h3,h2,figcaption')?.textContent || button.textContent;
      image = new Image();
      image.alt = original.alt;
      image.decoding = 'async';
      if (original.width) image.width = original.naturalWidth || original.width;
      if (original.height) image.height = original.naturalHeight || original.height;
      listen(image, 'load', fit, controller.signal, { once: true });
      image.src = original.currentSrc || original.src;
      pan.replaceChildren(image);
      dialog.showModal();
      previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      fit();
    }, controller.signal);
    listen(dialog.querySelector('[data-bf-visual-close]'), 'click', () => dialog.close(), controller.signal);
    listen(dialog, 'close', restore, controller.signal);
    listen(zoomIn, 'click', () => applyZoom(zoom + 0.5), controller.signal);
    listen(zoomOut, 'click', () => applyZoom(zoom - 0.5), controller.signal);
    listen(dialog.querySelector('[data-bf-visual-reset]'), 'click', fit, controller.signal);
    listen(dialog, 'click', event => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    }, controller.signal);
    listen(pan, 'pointerdown', event => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || zoom <= 1) return;
      dragging = { id: event.pointerId, x: event.clientX, y: event.clientY, left: pan.scrollLeft, top: pan.scrollTop };
      pan.setPointerCapture(event.pointerId);
      pan.dataset.dragging = 'true';
      event.preventDefault();
    }, controller.signal);
    listen(pan, 'pointermove', event => {
      if (!dragging || dragging.id !== event.pointerId) return;
      pan.scrollLeft = dragging.left - event.clientX + dragging.x;
      pan.scrollTop = dragging.top - event.clientY + dragging.y;
    }, controller.signal);
    for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) {
      listen(pan, type, () => { dragging = undefined; pan.dataset.dragging = 'false'; }, controller.signal);
    }
    listen(window, 'resize', () => { if (dialog.open) fit(); }, controller.signal);
    listen(controller.signal, 'abort', () => {
      if (dialog.open) dialog.close();
      restore();
      root.removeAttribute('data-enhanced');
    }, undefined, { once: true });
    root.dataset.enhanced = 'true';
  }

  function scan(scope = document) {
    if (scope instanceof Element && scope.matches('#bf-blog-complete')) initialize(scope);
    scope.querySelectorAll('#bf-blog-complete').forEach(initialize);
  }

  listen(document, 'shopify:section:load', event => scan(event.target), lifecycle.signal);
  listen(document, 'shopify:section:unload', event => {
    for (const [root, controller] of roots) {
      if (event.target.contains(root)) { controller.abort(); roots.delete(root); }
    }
  }, lifecycle.signal);
  listen(lifecycle.signal, 'abort', () => {
    for (const controller of roots.values()) controller.abort();
    roots.clear();
  }, undefined, { once: true });
  window.bfBlogCompleteVisuals = lifecycle;
  scan();
})();
